import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { TrendingUp, Loader2, CheckCircle, XCircle, AlertCircle, Package, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface CaseStudyData {
  id: string;
  industry: string;
  country: string | null;
  product_count: number;
  ctr_change_pct: number;
  score_before: number;
  score_after: number;
  measurement_days: number;
  optimization_date: string;
  consent_status: string;
  brand_name: string | null;
  brand_logo_url: string | null;
  brand_website: string | null;
}

export default function CaseStudyConsent() {
  const { t } = useTranslation('caseStudy');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const autoAction = searchParams.get('action');

  const [caseStudy, setCaseStudy] = useState<CaseStudyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  const [brandName, setBrandName] = useState('');
  const [brandLogoUrl, setBrandLogoUrl] = useState('');
  const [brandWebsite, setBrandWebsite] = useState('');

  // Fetch case study data
  useEffect(() => {
    async function fetchData() {
      try {
        const body: Record<string, string> = { action: 'get' };
        if (token) body.consent_token = token;

        const res = await supabase.functions.invoke('case-study-consent', {
          body,
        });

        if (res.error || !res.data?.id) {
          setError(true);
          return;
        }

        setCaseStudy(res.data);

        // Auto-decline from email link
        if (autoAction === 'decline' && res.data.consent_status === 'pending') {
          handleDecline(res.data);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [token]);

  async function handleApprove() {
    if (!brandName.trim()) return;
    setSubmitting(true);

    try {
      const body: Record<string, any> = {
        action: 'approve',
        brand_name: brandName.trim(),
        brand_logo_url: brandLogoUrl.trim() || undefined,
        brand_website: brandWebsite.trim() || undefined,
      };
      if (token) body.consent_token = token;

      const res = await supabase.functions.invoke('case-study-consent', { body });

      if (res.error) throw res.error;

      setCaseStudy(prev => prev ? { ...prev, consent_status: 'approved', brand_name: brandName.trim() } : null);
      toast.success(t('toasts.approved'));
    } catch {
      toast.error(t('toasts.error'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDecline(csData?: CaseStudyData) {
    setSubmitting(true);

    try {
      const body: Record<string, any> = { action: 'decline' };
      if (token) body.consent_token = token;

      const res = await supabase.functions.invoke('case-study-consent', { body });

      if (res.error) throw res.error;

      setCaseStudy(prev => prev ? { ...prev, consent_status: 'declined' } : null);
      toast.success(t('toasts.declined'));
    } catch {
      toast.error(t('toasts.error'));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !caseStudy) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-lg font-semibold">{t('errorTitle')}</h2>
            <p className="text-muted-foreground mt-2">{t('errorDescription')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isResponded = caseStudy.consent_status === 'approved' || caseStudy.consent_status === 'declined';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{t('title')}</CardTitle>
          <CardDescription>{t('subtitle')}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 rounded-lg bg-muted/50">
              <div className="flex items-center justify-center gap-1">
                <TrendingUp className="h-4 w-4 text-success" />
                <span className="text-xl font-bold text-success-ink">+{caseStudy.ctr_change_pct}%</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{t('stats.ctrImprovement')}</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-muted/50">
              <div className="flex items-center justify-center gap-1">
                <Package className="h-4 w-4 text-primary" />
                <span className="text-xl font-bold">{caseStudy.product_count}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{t('stats.products')}</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-muted/50">
              <div className="flex items-center justify-center gap-1">
                <Calendar className="h-4 w-4 text-primary" />
                <span className="text-xl font-bold">{caseStudy.measurement_days}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{t('stats.days')}</p>
            </div>
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2 justify-center">
            <Badge variant="secondary">{caseStudy.industry}</Badge>
            {caseStudy.country && <Badge variant="outline">{caseStudy.country}</Badge>}
          </div>

          <Separator />

          {/* Already responded */}
          {isResponded && (
            <div className="text-center py-4">
              {caseStudy.consent_status === 'approved' ? (
                <>
                  <CheckCircle className="h-12 w-12 mx-auto text-success mb-3" />
                  <p className="font-medium">{t('success.approved')}</p>
                </>
              ) : (
                <>
                  <XCircle className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <p className="font-medium">{t('success.declined')}</p>
                </>
              )}
            </div>
          )}

          {/* Pending consent */}
          {caseStudy.consent_status === 'pending' && (
            <div className="space-y-6">
              {/* Approve section */}
              <div className="space-y-4 p-4 rounded-lg border border-primary/20 bg-primary/5">
                <div>
                  <h3 className="font-semibold">{t('approve.title')}</h3>
                  <p className="text-sm text-muted-foreground">{t('approve.description')}</p>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label htmlFor="brandName">{t('approve.brandName')} *</Label>
                    <Input
                      id="brandName"
                      value={brandName}
                      onChange={(e) => setBrandName(e.target.value)}
                      placeholder={t('approve.brandNamePlaceholder')}
                    />
                  </div>
                  <div>
                    <Label htmlFor="logoUrl">{t('approve.logoUrl')} {t('approve.optional')}</Label>
                    <Input
                      id="logoUrl"
                      value={brandLogoUrl}
                      onChange={(e) => setBrandLogoUrl(e.target.value)}
                      placeholder={t('approve.logoUrlPlaceholder')}
                    />
                  </div>
                  <div>
                    <Label htmlFor="website">{t('approve.website')} {t('approve.optional')}</Label>
                    <Input
                      id="website"
                      value={brandWebsite}
                      onChange={(e) => setBrandWebsite(e.target.value)}
                      placeholder={t('approve.websitePlaceholder')}
                    />
                  </div>
                </div>

                <Button
                  onClick={handleApprove}
                  disabled={submitting || !brandName.trim()}
                  className="w-full"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {t('approve.button')}
                </Button>
              </div>

              <Separator />

              {/* Decline section */}
              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">{t('decline.description')}</p>
                <Button
                  variant="outline"
                  onClick={() => handleDecline()}
                  disabled={submitting}
                >
                  {t('decline.button')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
